import React, { useEffect, useState } from 'react';
import { Alert, Col, Divider, Row } from 'antd';
import { useAtomValue, useAtom } from 'jotai';
import { fullSensitivityConfigAtom, selectedProductAtom } from '../../atoms';
import { StyledInput, StyledUnit, StyledConfigLabel, StyledInputWrapper } from './styled';
import { getMetricDisplayName } from '@src/utils/metrics';
import { useSelector } from 'react-redux';
import { aiAlerts1minPersistenceSelector } from '@src/store/common/selectors';
import { checkErrors } from '../../popup/errorUtils';

/*
  these metrics have no unit, for now we are not supporting
  app custom metric with no unit in ai alerts
  but when we do we will have to treat those metric as special metric
  or find some general method to do this
*/
const specialMetrics = ['ConcurrentPlays', 'AppUniqueDevices'];

const SensitivityRows = ({ sensitivity }) => {
  const selectedProduct = useAtomValue(selectedProductAtom);
  const [sensitivityDetails, setSensitivityDetails] = useAtom(fullSensitivityConfigAtom);
  const [errorMsg, setErrorMsg] = useState('');
  const is1minEnabled = useSelector(aiAlerts1minPersistenceSelector);
  const minVal = is1minEnabled ? 1 : 3;

  const onChangeKey =
    ({ key }) =>
    (value) => {
      setSensitivityDetails((prev) => {
        return prev.map((sensitivityConfig) => {
          if (sensitivityConfig.metric === sensitivity.metric) {
            return { ...sensitivityConfig, [key]: value };
          }
          return sensitivityConfig;
        });
      });
    };

  useEffect(() => {
    if (sensitivityDetails && sensitivity) {
      const sensitivityConfig = sensitivityDetails.find((m) => m.metric === sensitivity.metric);
      if (sensitivityConfig) {
        setErrorMsg(checkErrors(sensitivityConfig));
      }
    }
  }, [sensitivityDetails]);

  const metricName = getMetricDisplayName({
    metricKey: sensitivity.metricKey,
    context: selectedProduct,
  });

  return (
    <>
      <Divider orientation="left" orientationMargin="0" style={{ margin: '10px 0' }}>
        {metricName}
      </Divider>
      <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 16]}>
        <Col span={9}>
          <StyledConfigLabel>Persistence exceeds</StyledConfigLabel>
        </Col>

        <Col span={5}>
          <StyledInputWrapper>
            <StyledInput
              min={minVal}
              max={60}
              onChange={onChangeKey({ key: 'sustain' })}
              value={sensitivity.sustain}
            />
            <StyledUnit>min</StyledUnit>
          </StyledInputWrapper>
        </Col>

        <Col span={5}>
          <StyledInputWrapper>
            <StyledInput
              min={minVal}
              max={60}
              onChange={onChangeKey({ key: 'warningSustain' })}
              value={sensitivity.warningSustain}
            />
            <StyledUnit>min</StyledUnit>
          </StyledInputWrapper>
        </Col>

        <Col span={5}>
          <StyledInputWrapper>
            <StyledInput
              min={minVal}
              max={60}
              onChange={onChangeKey({ key: 'criticalSustain' })}
              value={sensitivity.criticalSustain}
            />
            <StyledUnit>min</StyledUnit>
          </StyledInputWrapper>
        </Col>
      </Row>

      {specialMetrics.includes(sensitivity.metricKey) ? (
        <div style={{ marginTop: '12px' }}>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={9}>
              <StyledConfigLabel>Concurrency declines</StyledConfigLabel>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  min={0}
                  max={100}
                  onChange={onChangeKey({ key: 'sustainPcpDrop' })}
                  value={sensitivity.sustainPcpDrop}
                />
                <StyledUnit>%</StyledUnit>
              </StyledInputWrapper>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  min={0}
                  max={100}
                  onChange={onChangeKey({ key: 'warningPcpDrop' })}
                  value={sensitivity.warningPcpDrop}
                />
                <StyledUnit>%</StyledUnit>
              </StyledInputWrapper>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  min={0}
                  max={100}
                  onChange={onChangeKey({ key: 'criticalPcpDrop' })}
                  value={sensitivity.criticalPcpDrop}
                />
                <StyledUnit>%</StyledUnit>
              </StyledInputWrapper>
            </Col>
          </Row>
        </div>
      ) : (
        <div style={{ marginTop: '12px' }}>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={9}>
              <StyledConfigLabel>Total Impacted Devices exceeds</StyledConfigLabel>
            </Col>

            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  min={5}
                  max={1000000}
                  onChange={onChangeKey({ key: 'impactedUnique' })}
                  value={sensitivity.impactedUnique}
                />
                <StyledUnit>#</StyledUnit>
              </StyledInputWrapper>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  min={5}
                  max={1000000}
                  onChange={onChangeKey({ key: 'warningImpactedUnique' })}
                  value={sensitivity.warningImpactedUnique}
                />
                <StyledUnit>#</StyledUnit>
              </StyledInputWrapper>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  min={5}
                  max={1000000}
                  onChange={onChangeKey({ key: 'criticalImpactedUnique' })}
                  value={sensitivity.criticalImpactedUnique}
                />
                <StyledUnit>#</StyledUnit>
              </StyledInputWrapper>
            </Col>
          </Row>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={9}>
              <StyledConfigLabel>% Impacted Devices of Root Cause exceeds</StyledConfigLabel>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  min={0}
                  max={100}
                  onChange={onChangeKey({ key: 'impactedUniquePct' })}
                  value={sensitivity.impactedUniquePct}
                />
                <StyledUnit>%</StyledUnit>
              </StyledInputWrapper>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  min={0}
                  max={100}
                  onChange={onChangeKey({ key: 'warningImpactedUniquePct' })}
                  value={sensitivity.warningImpactedUniquePct}
                />
                <StyledUnit>%</StyledUnit>
              </StyledInputWrapper>
            </Col>

            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  min={0}
                  max={100}
                  onChange={onChangeKey({ key: 'criticalImpactedUniquePct' })}
                  value={sensitivity.criticalImpactedUniquePct}
                />
                <StyledUnit>%</StyledUnit>
              </StyledInputWrapper>
            </Col>
          </Row>
        </div>
      )}
      {errorMsg ? <Alert message={errorMsg} type={'error'} /> : null}
    </>
  );
};

export default React.memo(SensitivityRows);
